import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { MenuTriggerForDirective } from '@vsolv/vectors-ui/menu';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { ClaimItemHoldService, ClaimItemOwnerOption } from '../../services';

/** Staff and service provider ids live in different tables, so key owners by type and id. */
const ownerKey = (owner: ClaimItemOwnerOption) => `${owner.ownerType}:${owner.ownerId}`;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-hold-dialog',
  templateUrl: './claim-item-hold.dialog.html',
  standalone: false,
})
export class ClaimItemHoldDialog {
  constructor(
    private formBuilder: FormBuilder,
    private holdSvc: ClaimItemHoldService,
    private toastSvc: ToastService
  ) {}

  readonly config = CLAIM_ITEM_CONTROLS_CONFIG;
  readonly ownerKey = ownerKey;

  @ViewChild(DialogComponent) dialog?: DialogComponent;

  @Input() claim: Claim.Model | null = null;
  @Input() item: Claim.ClaimItem.Model | null = null;
  @Input() lines: Claim.ClaimItem.Line.Model[] = [];

  @Output() saved = new EventEmitter<string>();

  types = Object.values(Claim.ClaimItem.Hold.Type);
  owners: ClaimItemOwnerOption[] = [];
  saving = false;

  form = this.formBuilder.group({
    type: [Claim.ClaimItem.Hold.Type.INSPECTION as Claim.ClaimItem.Hold.Type, Validators.required],
    claimItemLineId: [null as string | null],
    requestedEvidence: ['', [Validators.required, Validators.pattern(/\S/)]],
    owner: [null as string | null, Validators.required],
    dueDate: ['', Validators.required],
  });

  get activeLines() {
    return this.lines.filter(line => !line.deleted);
  }

  async openDialog() {
    this.form.reset({
      type: Claim.ClaimItem.Hold.Type.INSPECTION,
      claimItemLineId: null,
      requestedEvidence: '',
      owner: null,
      dueDate: '',
    });
    this.dialog?.open();

    if (this.claim) {
      const response = await this.holdSvc.owners(this.claim.id).catch(() => null);
      this.owners = response?.items ?? [];
    }
  }

  close() {
    this.dialog?.close();
  }

  /** The due date as a Date, for the calendar. */
  pickerDate() {
    const value = this.form.value.dueDate;
    return value ? new Date(`${value}T00:00:00`) : null;
  }

  pickDueDate(date: Date | null, trigger: MenuTriggerForDirective) {
    this.form.controls.dueDate.setValue(date ? formatDate(date, 'yyyy-MM-dd', 'en') : '');
    this.form.controls.dueDate.markAsDirty();
    trigger.close();
  }

  async save() {
    if (!this.claim || !this.item || this.form.invalid || this.saving) return;

    const value = this.form.value;
    const owner = this.owners.find(candidate => ownerKey(candidate) === value.owner);
    if (!owner) return;

    this.saving = true;
    try {
      const result = await this.holdSvc
        .open(this.claim.id, this.item.id, {
          type: value.type as Claim.ClaimItem.Hold.Type,
          claimItemLineId: value.claimItemLineId || null,
          requestedEvidence: (value.requestedEvidence ?? '').trim(),
          ownerType: owner.ownerType,
          ownerId: owner.ownerId,
          dueDate: new Date(`${value.dueDate}T00:00:00`),
        })
        .catch((response: HttpErrorResponse) => {
          const message = response?.error?.message;
          this.toastSvc.show({
            type: 'error',
            title: this.config.holdDialog.errorTitle,
            text: Array.isArray(message) ? message.join(' ') : message,
          });
          return null;
        });

      if (!result) return;

      this.toastSvc.show({ type: 'success', ...this.config.holdDialog.saved });
      this.saved.emit(result.id);
      this.close();
    } finally {
      this.saving = false;
    }
  }
}
